<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DespesaStatus {
  despesa_status_id: Id;
  despesa_status_titulo?: string;
  [key: string]: unknown;
}

interface CellParams {
  data: DespesaStatus;
  value?: unknown;
}

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [statusId: Id];
  delete: [status: DespesaStatus];
}>();

const { t } = useI18n();
const { initialized } = useFiltro();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed(() => '/index.php/config/despesas_status/carrega_status');

const tableColumns = computed(() => {
  return [
    {
      data: 'despesa_status_id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'despesa_status_titulo',
      title: t('Título'),
      width: 250,
      sortable: true,
      filterType: 'text',
      flex: 1
    },
    {
      data: null,
      title: t('Ações'),
      width: 120,
      sortable: false,
      cellRenderer: (params: CellParams) => {
        const container = document.createElement('div');
        container.className = 'action-buttons';

        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'p-button p-button-danger p-button-sm p-button-icon-only';
        deleteBtn.setAttribute('data-testid', `despesas-status-btn-delete-${params.data.despesa_status_id}`);
        deleteBtn.innerHTML = '<i class="pi pi-trash"></i>';
        deleteBtn.addEventListener('click', () => {
          handleDeleteClick(params.data);
        });

        container.appendChild(deleteBtn);

        return container;
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `status_despesas_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV')
  };
});

const loadData = (): void => {
  const stack = new Error().stack;
  log('DespesasStatusTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  datatableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('DespesasStatusTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t('Carregados') + ` ${data.length} ` + t('status'));
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('DespesasStatusTable', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || String(err);
  eventBus.emit('globalMessage.error', t('Erro ao carregar status: ') + message);
};

const handleRowClick = (rowData?: DespesaStatus): void => {
  log('DespesasStatusTable', 'handleRowClick', 'Row clicked', rowData?.despesa_status_id || null);
};

const handleEditClick = (statusId: Id): void => {
  log('DespesasStatusTable', 'handleEditClick', 'Edit clicked for ID', statusId);
  emit('edit', statusId);
};

const handleDeleteClick = (status: DespesaStatus): void => {
  log('DespesasStatusTable', 'handleDeleteClick', 'Delete clicked', status);
  emit('delete', status);
};

const handleFilterUpdated = (): void => {
  log('DespesasStatusTable', 'event:filter.updated', 'Received');
  loadData();
};

const handleStatusSaved = (): void => {
  log('DespesasStatusTable', 'event:despesas-status.saved', 'Received');
  loadData();
};

const handleStatusDeleted = (): void => {
  log('DespesasStatusTable', 'event:despesas-status.deleted', 'Received');
  loadData();
};

const handleStatusRefresh = (): void => {
  log('DespesasStatusTable', 'event:refresh-despesas-status', 'Received');
  loadData();
};

onMounted(() => {
  log('DespesasStatusTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  log('DespesasStatusTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('despesas-status.saved', handleStatusSaved);
  eventBus.on('despesas-status.deleted', handleStatusDeleted);
  eventBus.on('despesas-status.refresh', handleStatusRefresh);
});

onBeforeUnmount(() => {
  log('DespesasStatusTable', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('despesas-status.saved', handleStatusSaved);
  eventBus.off('despesas-status.deleted', handleStatusDeleted);
  eventBus.off('despesas-status.refresh', handleStatusRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
:deep(.action-buttons) {
  display: flex;
  gap: 5px;
  justify-content: center;
}

:deep(.p-button) {
  border-radius: 4px;
  transition: all 0.2s ease;
}

:deep(.p-button-danger) {
  background-color: #dd4b39;
  border-color: #dd4b39;
}

:deep(.p-button-danger:hover) {
  background-color: #d33724;
  border-color: #d33724;
}

:deep(.p-button-sm) {
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
}

:deep(.p-button-icon-only) {
  width: 2rem;
  padding: 0.4rem;
}
</style>
